import type { HubSlowRequestState } from "./hubBackendDegraded";

/** Truncate displayed paths (including query strings) so the banner stays readable. */
export const SLOW_REQUEST_PATH_DISPLAY_MAX_CHARS = 120;

export const DB_POOL_HOLDERS_STREAM_PATH = "/api/hub/db-pool-holders/stream";

export const POOL_HOLDER_SECTION_TITLE = "Holding the database slots";

export const POOL_HOLDER_SECTION_HELP =
  "This API process has ten database query slots. Three may hold cost, slow, search, or fast work. Five more may hold slow, search, or fast. One is search or fast. The last is fast only. Hub-cache LISTEN uses its own connection, not one of these ten. Production-cost report SQL uses a cost seat, not extra connections. Ingest SQL uses a slow seat. When several people are waiting, the next free slot goes to whoever currently holds the fewest. Megaventory HTTP is a separate two-seat limiter. This list is from this API process only.";

export const POOL_LISTEN_OWN_CONNECTION_HELD_TEXT =
  "Hub cache LISTEN is on its own connection. It is not one of these ten query seats.";

export const POOL_LISTEN_OWN_CONNECTION_IDLE_TEXT =
  "Hub cache LISTEN is not attached in this process (local stacks skip it).";

export const PROCESS_WAITER_SECTION_TITLE = "Waiting in this API process";

export const PROCESS_WAITER_SECTION_HELP =
  "These Fastify requests and background queries are blocked on pool admission or have stalled while acquiring a connection. In-flight checkouts that pass admission quickly are hidden. They include every user and every browser on this process, not only this tab.";

export const PROCESS_WAITER_EMPTY_TEXT =
  "No request is waiting for a database slot in this API process.";

export const MEGAVENTORY_HOLDER_SECTION_TITLE = "Holding the Megaventory HTTP slots";

export const MEGAVENTORY_HOLDER_SECTION_HELP =
  "Megaventory has two HTTP seats in this API process. One may hold a GET or a POST. The other is POST only. Megaventory Get RPCs (ProductGet, DocumentGet, and similar) use the GET-or-POST seat even when the HTTP method is POST. Writes may use either seat. Rate-limit retries stay on the same seat.";

export const MEGAVENTORY_HOLDER_EMPTY_TEXT =
  "No Megaventory request is in flight in this API process.";

export const MEGAVENTORY_WAITER_SECTION_TITLE = "Waiting for a Megaventory HTTP slot";

export const MEGAVENTORY_WAITER_SECTION_HELP =
  "These Megaventory calls are waiting for a seat that allows their RPC (GET-or-POST for reads, either seat for writes). They do not occupy a database slot while they wait.";

export const MEGAVENTORY_WAITER_EMPTY_TEXT =
  "No Megaventory request is waiting for an HTTP slot in this API process.";

export const BROWSER_WAITER_SECTION_TITLE = "Waiting in this tab";

export const BROWSER_WAITER_SECTION_HELP =
  "These are this tab's own requests. They may still be on the way to the API, running a query, or waiting for a slot. The list above is every waiter on this API process.";

export const BROWSER_WAITER_EMPTY_TEXT = "No request from this tab is still in flight.";

/** Overlay queue for this tab's long `apiFetch` calls (open longer than eight seconds). */
export const THIS_TAB_QUEUE_ID = "this-tab";

export const THIS_TAB_QUEUE_HELP =
  "These are this tab's own requests that have been open longer than eight seconds. They may still be on the way to the API, running a query, or waiting for a slot. They do not need a database or Megaventory seat to appear here.";

export const POOL_HOLDER_STREAM_DISCONNECTED_TEXT =
  "Holder stream is not connected. Holders unknown until the stream opened at sign-in pushes a snapshot.";

export const POOL_HOLDER_STREAM_RECONNECTING_TEXT =
  "Holder stream reconnecting. Showing the last snapshot from this API process.";

export const POOL_HOLDER_EMPTY_TEXT =
  "No checkout is currently recorded in this API process.";

/**
 * Longest-prefix-first. Keep this small; unknown paths show the path only.
 */
const FRIENDLY_PATH_LABELS: readonly { prefix: string; label: string }[] = [
  { prefix: "/api/mcp", label: "MCP" },
  { prefix: "/api/mrp/purchase-orders/", label: "Purchase order detail" },
  { prefix: "/api/mrp/purchase-orders", label: "Purchase orders" },
  { prefix: "/api/mrp/", label: "MRP" },
  { prefix: "/api/bom/", label: "Bill of materials" },
  { prefix: "/api/manufacturing/", label: "Manufacturing" },
  { prefix: "/api/shipping/", label: "Shipping" },
  { prefix: "/api/documents/", label: "Documents" },
  { prefix: "/api/accounting/suntronic-invoices", label: "Manufacturer invoices" },
  { prefix: "/api/accounting/", label: "Accounting" },
  { prefix: "/api/hub/presence", label: "Page presence" },
  { prefix: "/api/notifications/unread-count", label: "Unread notification count" },
  { prefix: "/api/notifications", label: "Notifications" },
];

const SENSITIVE_QUERY_KEYS = new Set([
  "token",
  "access_token",
  "authorization",
  "apikey",
  "api_key",
  "secret",
  "password",
]);

export type HubDbPoolHolderKind = "listen" | "http" | "background";

export interface HubDbPoolHolderRow {
  id: number;
  kind: HubDbPoolHolderKind;
  purpose: string;
  httpMethod: string | null;
  httpPath: string | null;
  acquiredAtMs: number;
  heldMs: number;
  userEmail?: string | null;
  userDisplayName?: string | null;
  checkoutClass?: string;
}

export interface HubDbPoolWaiterRow {
  id: number;
  kind: HubDbPoolHolderKind;
  waitPhase?: "admission" | "connecting";
  purpose: string;
  httpMethod: string | null;
  httpPath: string | null;
  userEmail: string | null;
  userDisplayName: string | null;
  waitingSinceMs: number;
  waitedMs: number;
}

export interface HubProcessQueueSeat {
  kind: string;
  label: string;
  occupant: HubDbPoolHolderRow | null;
}

export type HubProcessQueueId = "supabase-query" | "megaventory-http" | "this-tab";

export interface HubProcessQueueSnapshot {
  id: HubProcessQueueId;
  inUse: number;
  untrackedInUse?: number;
  max: number | null;
  holders: HubDbPoolHolderRow[];
  waiters: HubDbPoolWaiterRow[];
  seats?: HubProcessQueueSeat[];
}

export interface HubDbPoolHolderSnapshot {
  source: "this_api_process";
  sourceNote: string;
  capturedAtMs: number;
  pool: {
    totalCount: number;
    idleCount: number;
    waitingCount: number;
    max: number;
  } | null;
  holders: HubDbPoolHolderRow[];
  waiters: HubDbPoolWaiterRow[];
  queues?: HubProcessQueueSnapshot[];
  /** Dedicated hub-cache LISTEN connection (not a query-pool seat). */
  listenHeld?: boolean;
}

export interface HubProcessQueueCopy {
  holderTitle: string;
  holderHelp: string;
  holderEmpty: string;
  waiterTitle: string;
  waiterHelp: string;
  waiterEmpty: string;
  chipLabel: string;
}

export function hubProcessQueueCopy(id: string): HubProcessQueueCopy {
  if (id === "megaventory-http") {
    return {
      holderTitle: MEGAVENTORY_HOLDER_SECTION_TITLE,
      holderHelp: MEGAVENTORY_HOLDER_SECTION_HELP,
      holderEmpty: MEGAVENTORY_HOLDER_EMPTY_TEXT,
      waiterTitle: MEGAVENTORY_WAITER_SECTION_TITLE,
      waiterHelp: MEGAVENTORY_WAITER_SECTION_HELP,
      waiterEmpty: MEGAVENTORY_WAITER_EMPTY_TEXT,
      chipLabel: "MV",
    };
  }
  if (id === THIS_TAB_QUEUE_ID) {
    return {
      holderTitle: BROWSER_WAITER_SECTION_TITLE,
      holderHelp: THIS_TAB_QUEUE_HELP,
      holderEmpty: BROWSER_WAITER_EMPTY_TEXT,
      waiterTitle: BROWSER_WAITER_SECTION_TITLE,
      waiterHelp: THIS_TAB_QUEUE_HELP,
      waiterEmpty: BROWSER_WAITER_EMPTY_TEXT,
      chipLabel: "TAB",
    };
  }
  return {
    holderTitle: POOL_HOLDER_SECTION_TITLE,
    holderHelp: POOL_HOLDER_SECTION_HELP,
    holderEmpty: POOL_HOLDER_EMPTY_TEXT,
    waiterTitle: PROCESS_WAITER_SECTION_TITLE,
    waiterHelp: PROCESS_WAITER_SECTION_HELP,
    waiterEmpty: PROCESS_WAITER_EMPTY_TEXT,
    chipLabel: "DB",
  };
}

export function listHubProcessQueues(snapshot: HubDbPoolHolderSnapshot): HubProcessQueueSnapshot[] {
  if (snapshot.queues != null && snapshot.queues.length > 0) {
    return snapshot.queues;
  }
  const inUse =
    snapshot.pool != null ? Math.max(0, snapshot.pool.totalCount - snapshot.pool.idleCount) : snapshot.holders.length;
  return [
    {
      id: "supabase-query",
      inUse,
      max: snapshot.pool?.max ?? null,
      holders: snapshot.holders,
      waiters: snapshot.waiters ?? [],
    },
  ];
}

export const EMPTY_HUB_DB_POOL_HOLDER_SNAPSHOT: HubDbPoolHolderSnapshot = {
  source: "this_api_process",
  sourceNote: "",
  capturedAtMs: 0,
  pool: null,
  holders: [],
  waiters: [],
};

/** Oldest long request first so the overlay matches the banner's elapsed headline. */
export function thisTabSlowRequestQueue(
  slowRequests: readonly HubSlowRequestState[],
  nowMs: number,
): HubProcessQueueSnapshot {
  const waiters: HubDbPoolWaiterRow[] = slowRequests
    .filter((request) => !isDbPoolHoldersApiPath(request.path))
    .slice()
    .sort((left, right) => left.startedAtMs - right.startedAtMs)
    .map((request) => ({
      id: request.id,
      kind: "http",
      purpose: friendlySlowRequestLabel(request.path) ?? "This tab",
      httpMethod: null,
      httpPath: request.path,
      userEmail: null,
      userDisplayName: null,
      waitingSinceMs: request.startedAtMs,
      waitedMs: Math.max(0, nowMs - request.startedAtMs),
    }));
  return {
    id: THIS_TAB_QUEUE_ID,
    inUse: waiters.length,
    max: null,
    holders: [],
    waiters,
  };
}

/** This tab's long requests first so the overlay shows them without scrolling past empty seats. */
export function overlayProcessQueues(
  snapshot: HubDbPoolHolderSnapshot | null,
  slowRequests: readonly HubSlowRequestState[],
  nowMs: number,
): HubProcessQueueSnapshot[] {
  const server = snapshot != null ? listHubProcessQueues(snapshot) : [];
  return [thisTabSlowRequestQueue(slowRequests, nowMs), ...server];
}

export function processQueueShowsHolderSection(queue: HubProcessQueueSnapshot): boolean {
  return queue.id !== THIS_TAB_QUEUE_ID;
}

export function visibleHubProcessQueues(
  queues: readonly HubProcessQueueSnapshot[],
  hiddenIds: readonly string[],
): HubProcessQueueSnapshot[] {
  if (queues.length === 0) {
    return [];
  }
  const hidden = new Set(hiddenIds);
  const visible = queues.filter((queue) => !hidden.has(queue.id));
  return visible.length > 0 ? visible : [...queues];
}

export function toggleHubQueueHidden(hiddenIds: readonly string[], queueId: string): string[] {
  if (hiddenIds.includes(queueId)) {
    return hiddenIds.filter((id) => id !== queueId);
  }
  return [...hiddenIds, queueId];
}

function pathnameAndQuery(path: string): { pathname: string; search: string } {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) {
    return { pathname: path, search: "" };
  }
  return { pathname: path.slice(0, queryIndex), search: path.slice(queryIndex + 1) };
}

export function friendlySlowRequestLabel(path: string): string | null {
  const { pathname } = pathnameAndQuery(path);
  for (const entry of FRIENDLY_PATH_LABELS) {
    if (pathname === entry.prefix || pathname.startsWith(entry.prefix)) {
      return entry.label;
    }
  }
  return null;
}

function redactQuery(search: string): string {
  if (!search) {
    return "";
  }
  const params = new URLSearchParams(search);
  let changed = false;
  for (const key of [...params.keys()]) {
    if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
      params.set(key, "redacted");
      changed = true;
    }
  }
  return changed ? params.toString() : search;
}

export function displaySlowRequestPath(
  path: string,
  maxChars = SLOW_REQUEST_PATH_DISPLAY_MAX_CHARS,
): string {
  const { pathname, search } = pathnameAndQuery(path);
  const redactedSearch = redactQuery(search);
  const full = redactedSearch ? `${pathname}?${redactedSearch}` : pathname;
  if (full.length <= maxChars) {
    return full;
  }
  return `${full.slice(0, Math.max(0, maxChars - 3))}...`;
}

export function slowRequestElapsedSeconds(startedAtMs: number, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - startedAtMs) / 1_000));
}

export function isDbPoolHoldersApiPath(path: string): boolean {
  const { pathname } = pathnameAndQuery(path);
  return (
    pathname === DB_POOL_HOLDERS_STREAM_PATH || pathname === "/api/hub/db-pool-holders"
  );
}

export interface SlowRequestBannerRow {
  id: number;
  pathDisplay: string;
  elapsedSeconds: number;
  friendlyLabel: string | null;
}

export function slowRequestBannerRows(
  requests: readonly HubSlowRequestState[],
  nowMs: number,
): SlowRequestBannerRow[] {
  return requests
    .filter((request) => !isDbPoolHoldersApiPath(request.path))
    .map((request) => ({
      id: request.id,
      pathDisplay: displaySlowRequestPath(request.path),
      elapsedSeconds: slowRequestElapsedSeconds(request.startedAtMs, nowMs),
      friendlyLabel: friendlySlowRequestLabel(request.path),
    }));
}

export interface PoolHolderBannerRow {
  id: number;
  kind: HubDbPoolHolderKind;
  title: string;
  pathDisplay: string | null;
  userLabel: string | null;
  elapsedSeconds: number;
}

export function poolHolderCapacityText(snapshot: HubDbPoolHolderSnapshot): string | null {
  if (snapshot.pool === null) {
    return null;
  }
  const inUse = Math.max(0, snapshot.pool.totalCount - snapshot.pool.idleCount);
  return `${inUse} of ${snapshot.pool.max} slots in use.`;
}

export function queueWaitingCount(
  queue: HubProcessQueueSnapshot,
  snapshot: HubDbPoolHolderSnapshot,
): number {
  if (queue.id === "supabase-query") {
    return Math.max(queue.waiters.length, snapshot.pool?.waitingCount ?? 0);
  }
  return queue.waiters.length;
}

/** Pad occupancy counts to the digit width of `total` (`00/10/00` when total is 10). */
export function padQueueOccupancyCount(value: number, total: number): string {
  const width = String(Math.max(0, total)).length;
  return String(Math.max(0, value)).padStart(width, "0");
}

/**
 * Compact occupancy for one process queue: `db 00/10/00` is slots full / total / items in queue.
 */
export function queueOccupancyCaption(
  queue: HubProcessQueueSnapshot,
  snapshot: HubDbPoolHolderSnapshot,
): string {
  const label = hubProcessQueueCopy(queue.id).chipLabel.toLowerCase();
  if (queue.id === THIS_TAB_QUEUE_ID) {
    return `${label} ${queue.waiters.length}`;
  }
  const waiting = queueWaitingCount(queue, snapshot);
  if (queue.max == null || queue.max < 1) {
    return `${label} ${queue.inUse}/-/${waiting}`;
  }
  return `${label} ${padQueueOccupancyCount(queue.inUse, queue.max)}/${queue.max}/${padQueueOccupancyCount(waiting, queue.max)}`;
}

export const POOL_ADMIN_OVERLAY_OCCUPANCY_HOVER_HINT =
  "Slots full, total slots, items in queue. tab N is this tab's long requests.";

/** Collapsed admin overlay chip: every process queue as `tab 0; db 00/10/00; mv 0/2/0`. */
export function poolAdminOverlaySummary(
  snapshot: HubDbPoolHolderSnapshot | null,
  slowRequests: readonly HubSlowRequestState[] = [],
  nowMs = 0,
): string {
  if (snapshot == null) {
    const tabCaption = queueOccupancyCaption(
      thisTabSlowRequestQueue(slowRequests, nowMs),
      EMPTY_HUB_DB_POOL_HOLDER_SNAPSHOT,
    );
    return `queues connecting; ${tabCaption}`;
  }
  return overlayProcessQueues(snapshot, slowRequests, nowMs)
    .map((queue) => queueOccupancyCaption(queue, snapshot))
    .join("; ");
}


export function poolHolderBannerRowsFromHolders(
  holders: readonly HubDbPoolHolderRow[],
  nowMs: number,
): PoolHolderBannerRow[] {
  return holders.map((holder) => {
    const pathDisplay =
      holder.httpPath != null
        ? `${holder.httpMethod ? `${holder.httpMethod} ` : ""}${displaySlowRequestPath(holder.httpPath)}`
        : null;
    const friendly =
      holder.httpPath != null ? friendlySlowRequestLabel(holder.httpPath) : null;
    return {
      id: holder.id,
      kind: holder.kind,
      title: friendly ?? holder.purpose,
      pathDisplay,
      userLabel: processWaiterUserLabel({
        userDisplayName: holder.userDisplayName ?? null,
        userEmail: holder.userEmail ?? null,
      }),
      elapsedSeconds: Math.max(0, Math.floor((nowMs - holder.acquiredAtMs) / 1_000)),
    };
  });
}

export function poolHolderBannerRows(
  snapshot: HubDbPoolHolderSnapshot,
  nowMs = snapshot.capturedAtMs,
): PoolHolderBannerRow[] {
  return poolHolderBannerRowsFromHolders(snapshot.holders, nowMs);
}

export interface ProcessWaiterBannerRow {
  id: number;
  kind: HubDbPoolHolderKind;
  title: string;
  pathDisplay: string | null;
  userLabel: string | null;
  elapsedSeconds: number;
}

export function processWaiterUserLabel(waiter: {
  userDisplayName: string | null;
  userEmail: string | null;
}): string | null {
  const name = waiter.userDisplayName?.trim() ?? "";
  if (name !== "") {
    return name;
  }
  const email = waiter.userEmail?.trim() ?? "";
  if (email !== "") {
    return email;
  }
  return null;
}

export function processWaiterBannerRowsFromWaiters(
  waiters: readonly HubDbPoolWaiterRow[],
  nowMs: number,
): ProcessWaiterBannerRow[] {
  return waiters.map((waiter) => {
    const pathDisplay =
      waiter.httpPath != null
        ? `${waiter.httpMethod ? `${waiter.httpMethod} ` : ""}${displaySlowRequestPath(waiter.httpPath)}`
        : null;
    const friendly =
      waiter.httpPath != null ? friendlySlowRequestLabel(waiter.httpPath) : null;
    return {
      id: waiter.id,
      kind: waiter.kind,
      title: friendly ?? waiter.purpose,
      pathDisplay,
      userLabel: processWaiterUserLabel(waiter),
      elapsedSeconds: Math.max(0, Math.floor((nowMs - waiter.waitingSinceMs) / 1_000)),
    };
  });
}

export function processWaiterBannerRows(
  snapshot: HubDbPoolHolderSnapshot,
  nowMs = snapshot.capturedAtMs,
): ProcessWaiterBannerRow[] {
  return processWaiterBannerRowsFromWaiters(snapshot.waiters ?? [], nowMs);
}
